import React from 'react';
import { View } from 'react-native';
import { Text } from '@/components/ui/text';
import { useTranslation } from 'react-i18next';
import { Icon } from '~/global/components/icon/Icon';
import type { AdjustedNutrition } from '@fytness/shared';

export interface MealNutritionFactsProps {
  name: string;
  brand?: string | null;
  /** Optional data source label, e.g. "USDA". */
  source?: string | null;
  nutrition: AdjustedNutrition;
}

const formatGrams = (value: number | null) =>
  value != null ? `${Math.round(value * 10) / 10}g` : '-';

/**
 * Read-only "upper part" of a meal: name/brand, optional source, the 4-macro
 * grid and the highlighted total-kcal box. Shared by the read-only chat meal
 * cards (`present_meal`, `present_meal_logs`).
 */
export const MealNutritionFacts: React.FC<MealNutritionFactsProps> = ({
  name,
  brand,
  source,
  nutrition,
}) => {
  const { t } = useTranslation('meal');

  const macros = [
    { label: t('nutrition.carbs.longLabel'), value: nutrition.carbs },
    { label: t('nutrition.protein.longLabel'), value: nutrition.protein },
    { label: t('nutrition.fats.longLabel'), value: nutrition.fats },
    { label: t('nutrition.fiber.longLabel'), value: nutrition.fiber },
  ];

  return (
    <View className="flex flex-col gap-3">
      <View className="flex flex-col gap-1">
        <Text className="text-base font-sora-semibold text-text-primary">
          {name}
        </Text>
        {brand ? (
          <Text className="text-xs text-text-muted">{brand}</Text>
        ) : null}
      </View>

      {source ? (
        <View className="flex flex-row items-center justify-end gap-1">
          <Icon name="Database" size={12} className="text-text-muted" />
          <Text className="text-[11px] text-text-muted">
            {t('nutrition.source')}: {source}
          </Text>
        </View>
      ) : null}

      <View className="flex flex-row justify-between">
        {macros.map(({ label, value }) => (
          <View key={label} className="flex flex-col items-center gap-1">
            <Text className="text-sm font-sora-semibold text-text-primary">
              {formatGrams(value)}
            </Text>
            <Text className="text-[11px] text-text-muted">{label}</Text>
          </View>
        ))}
      </View>

      <View className="flex flex-row items-center justify-center gap-2 rounded-lg bg-surface-elevated px-4 py-3">
        <Text className="text-sm font-inter-medium text-text-muted">
          {t('nutrition.total')}:
        </Text>
        <Text className="text-lg font-sora-bold text-accent">
          {nutrition.calories != null
            ? `${Math.round(nutrition.calories)} ${t('nutrition.kcal.unit')}`
            : '-'}
        </Text>
      </View>
    </View>
  );
};
